import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

// Admin audit + revoke UI for the pass-claim workaround. Lets the admin
// see who claimed what (from Stripe/PayPal welcome-email URLs) and revoke
// individual claims in cases of chargeback or abuse. Revoking subtracts the
// credited scans from that user's counter (clamped at zero — never negative).
export default function PassClaimsPanel() {
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [busySlug, setBusySlug] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/admin/claims");
            setClaims(data.claims || []);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const revoke = async (claimId) => {
        if (!window.confirm("Revoke this claim? The user's free-scan counter will be reduced by the credited amount (clamped at 0).")) return;
        setBusySlug(claimId);
        setErr("");
        try {
            await api.post(`/admin/claims/${claimId}/revoke`);
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusySlug("");
        }
    };

    return (
        <div data-testid="pass-claims-panel">
            <div className="flex items-baseline justify-between mb-6">
                <div>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight">Pass Claims</h2>
                    <p className="text-[#c2c2c2] text-sm mt-2 max-w-2xl leading-relaxed">
                        Every time a buyer opens their claim URL and authenticates, we credit their
                        account with the pass&apos;s scans and log a row here. Use <strong>Revoke</strong>
                        {" "}if a chargeback lands or the URL was misused — it subtracts the credited
                        scans from the user&apos;s counter (never below zero) and marks the claim as
                        revoked.
                    </p>
                </div>
                <button
                    className="btn-secondary text-xs"
                    onClick={load}
                    disabled={loading}
                    data-testid="pass-claims-refresh"
                >
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>
            {err && (
                <div className="mb-6 border border-[#c95151] bg-[#1a0e0e] p-4 text-sm text-[#f0c9c9]" data-testid="pass-claims-error">
                    {err}
                </div>
            )}
            {loading && !claims.length && (
                <div className="text-[#7a7a7a] text-sm" data-testid="pass-claims-loading">Loading…</div>
            )}
            {!loading && !claims.length && (
                <div className="text-[#7a7a7a] text-sm" data-testid="pass-claims-empty">
                    No claims yet. When a buyer clicks their claim URL and signs in, the row will appear here.
                </div>
            )}
            {claims.length > 0 && (
                <div className="border border-[#2a2a2a] bg-[#141414] overflow-x-auto" data-testid="pass-claims-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-widest text-[#7a7a7a] border-b border-[#2a2a2a]">
                                <th className="text-left px-4 py-3">Email</th>
                                <th className="text-left px-4 py-3">Plan</th>
                                <th className="text-left px-4 py-3">Cycle</th>
                                <th className="text-right px-4 py-3">Scans</th>
                                <th className="text-left px-4 py-3">Claimed</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-right px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {claims.map((c) => (
                                <tr
                                    key={c.id}
                                    className="border-b border-[#1a1a1a] last:border-b-0"
                                    data-testid={`pass-claim-row-${c.id}`}
                                >
                                    <td className="px-4 py-3 text-[#e2e2e2]">{c.email}</td>
                                    <td className="px-4 py-3 text-[#e2e2e2]">{c.plan_display_name || c.plan_slug}</td>
                                    <td className="px-4 py-3 text-[#c2c2c2]">
                                        {c.cycle === "annual" ? "1-Year pass" : "1-Month pass"}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono">{c.scans_credited}</td>
                                    <td className="px-4 py-3 text-[#9a9a9a] text-xs">
                                        {new Date(c.claimed_at).toLocaleString(undefined, {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </td>
                                    <td className="px-4 py-3">
                                        {c.revoked ? (
                                            <span
                                                className="text-[#c95151] uppercase tracking-widest text-[10px]"
                                                data-testid={`pass-claim-status-revoked-${c.id}`}
                                            >
                                                Revoked
                                            </span>
                                        ) : (
                                            <span
                                                className="text-[#2ec5b6] uppercase tracking-widest text-[10px]"
                                                data-testid={`pass-claim-status-active-${c.id}`}
                                            >
                                                Active
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {!c.revoked && (
                                            <button
                                                className="text-[#c95151] hover:text-[#e26a6a] text-xs"
                                                onClick={() => revoke(c.id)}
                                                disabled={busySlug === c.id}
                                                data-testid={`pass-claim-revoke-${c.id}`}
                                            >
                                                {busySlug === c.id ? "Revoking…" : "Revoke"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
